/** Overview chart store ids (camelCase) → canonical `Kpi.name`. */
export const LEGACY_OVERVIEW_METRIC_ID_TO_KPI_NAME: Record<string, string> = {
  spend: 'Spend',
  cpa: 'CPA',
  roas: 'ROAS',
  conversions: 'Conversions',
  creatives: 'Creatives',
  stickRate: 'Stick Rate',
  ads: 'Ads',
  cpc: 'CPC',
  cpm: 'CPM',
  aov: 'AOV',
  ctr: 'CTR',
  cr: 'CR',
  thumbstop: 'Thumbstop',
  holdrate: 'Holdrate',
  engagement: 'Engagement',
  costPerLike: 'Likes',
  costPerCustom: 'Custom',
  costPerComment: 'Comments',
  costPerPostEng: 'Post Engagements',
  spendPerCreative: 'Spend/Creatives',
  spendPerSticked: 'Spend/Sticked',
  frequency: 'Frequency',
  spendPct: 'Spend %',
  creativesPct: 'Creatives %',
};

/** Drilldown My Brand column ids (snake_case) → canonical `Kpi.name`. */
export const LEGACY_DRILLDOWN_FIELD_TO_KPI_NAME: Record<string, string> = {
  spend: 'Spend',
  cpa: 'CPA',
  roas: 'ROAS',
  conversions: 'Conversions',
  value: 'Value',
  aov: 'AOV',
  creatives: 'Creatives',
  ads: 'Ads',
  cpc: 'CPC',
  cpm: 'CPM',
  ctr: 'CTR',
  cr: 'CR',
  thumbstop: 'Thumbstop',
  holdrate: 'Holdrate',
  frequency: 'Frequency',
  engagement: 'Engagement',
  cost_per_like: 'Likes',
  cost_per_custom: 'Custom',
  cost_per_comment: 'Comments',
  cost_per_post_eng: 'Post Engagements',
  spend_per_creative: 'Spend/Creatives',
  impressions: 'Impressions',
  link_click: 'Link Clicks',
  clicks: 'Clicks',
  video_play_action: 'Video Plays',
  video_thruplay_actions: 'Video Thruplays',
  avg_watch_time: 'Avg Watch Time',
  likes: 'Like Count',
  comments: 'Comment Count',
  post_engagements: 'Post Engagement Count',
  custom_event: 'Custom Event Count',
};

export function migrateLegacyKpiId(id: string): string {
  return LEGACY_OVERVIEW_METRIC_ID_TO_KPI_NAME[id]
    ?? LEGACY_DRILLDOWN_FIELD_TO_KPI_NAME[id]
    ?? id;
}

/** Ordered chart-series tokens stored on KPI rows. Never persist a hex. */
export const KPI_COLOR_TOKENS = [
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
  'chart-7',
  'chart-8',
  'chart-9',
  'chart-10',
  'chart-11',
  'chart-12',
  'chart-13',
  'chart-14',
  'chart-15',
  'chart-16',
  'chart-17',
  'chart-18',
  'chart-19',
  'chart-20',
  'chart-21',
  'chart-22',
  'chart-23',
  'chart-24',
] as const;

export type KpiColorToken = (typeof KPI_COLOR_TOKENS)[number];

/** Tuple form for `z.enum` — same values as `KPI_COLOR_TOKENS`. */
export const KPI_COLOR_TOKEN_ENUM: [KpiColorToken, ...KpiColorToken[]] =
  KPI_COLOR_TOKENS as unknown as [KpiColorToken, ...KpiColorToken[]];

/**
 * System-wide KPI colors, from the DESIGN.md named KPI mapping (slots 1–6).
 *
 * Data only: these are resolved on read, never written to `kpis.color_token`.
 * A row carries a token only once someone picks one, so editing this map still
 * changes the color everywhere it has not been overridden.
 */
export const DEFAULT_ORG_KPI_COLOR_BY_NAME: Record<string, KpiColorToken> = {
  Spend: 'chart-1',
  CPA: 'chart-2',
  Creatives: 'chart-3',
  ROAS: 'chart-4',
  Conversions: 'chart-5',
  'Stick Rate': 'chart-6',
};

export function isKpiColorToken(value: string): value is KpiColorToken {
  return (KPI_COLOR_TOKENS as readonly string[]).includes(value);
}

export function defaultKpiColorToken(index: number): KpiColorToken {
  const length = KPI_COLOR_TOKENS.length;
  const normalized = ((index % length) + length) % length;
  return KPI_COLOR_TOKENS[normalized]!;
}

/** Deterministic palette slot from a KPI name — stable across reorder. */
export function defaultKpiColorTokenForName(name: string): KpiColorToken {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return defaultKpiColorToken(Math.abs(hash));
}

/**
 * Resolve the chart color token for a KPI row.
 *
 * `index` is kept for call-site compatibility but no longer drives the fallback
 * color — list position changes on reorder must not change identity color.
 */
export function resolveOrgKpiColorToken(
  name: string,
  _index: number,
  stored?: string | null,
): KpiColorToken {
  if (stored && isKpiColorToken(stored)) return stored;
  return DEFAULT_ORG_KPI_COLOR_BY_NAME[name] ?? defaultKpiColorTokenForName(name);
}

export function kpiColorCssVar(token: string | null | undefined, fallbackIndex = 0): string {
  return `var(--${resolveOrgKpiColorToken('', fallbackIndex, token)})`;
}
